/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type { FlagHistoryRow } from "~/features/feature-flags/shared/contracts"
import {
  inCategory,
  matchesSearch,
  type CategoryFilterValue,
} from "~/features/feature-flags/client/model/filters"
import type { FlagNames } from "~/features/feature-flags/client/model/flagPanel"
import { onOffLabel } from "~/features/feature-flags/client/model/labels"
import { FEATURE_FLAGS, flagName, isFeatureFlagKey } from "~/features/feature-flags/shared/registry"

export type HistoryChange = Omit<FlagHistoryRow, "id" | "changedAt">

export type HistoryFilters = {
  category: CategoryFilterValue
  search: string
}

/** Who a change was for: a company, a user or the default. */
export function historyTargetName(change: HistoryChange, names: FlagNames): string {
  if (change.scope === "default") return "Default"
  if (change.scope === "org") return names.companyName(change.targetId)
  return names.userName(change.targetId)
}

const valueLabel = (value: boolean | null, scope: string) => {
  if (value === null) return scope === "default" ? "Code default" : "Default"
  return onOffLabel(value)
}

export const historyChangeLabel = (change: HistoryChange) =>
  `${valueLabel(change.previousValue, change.scope)} → ${valueLabel(change.newValue, change.scope)}`

/** A flag no longer in the registry has no category, so only "All" keeps it. */
const inFlagCategory = (filter: CategoryFilterValue, flagKey: string) =>
  isFeatureFlagKey(flagKey) ? inCategory(filter, FEATURE_FLAGS[flagKey].category) : filter === "all"

const matchesHistorySearch = (search: string, change: HistoryChange, names: FlagNames) =>
  matchesSearch(
    search,
    change.flagKey,
    flagName(change.flagKey),
    historyTargetName(change, names),
    change.note ?? undefined,
    names.userName(change.changedBy),
  )

/**
 * Changes in the category, then matching the search on flag name or key, who it was for, the note
 * or who made it.
 */
export function filterHistory<T extends HistoryChange>(
  changes: readonly T[],
  { category, search }: HistoryFilters,
  names: FlagNames,
): T[] {
  return changes
    .filter((change) => inFlagCategory(category, change.flagKey))
    .filter((change) => matchesHistorySearch(search, change, names))
}
